import { useQuery } from '@tanstack/react-query';
import { format, isValid, parse } from 'date-fns';
import { motion } from 'framer-motion';
import { CreditCard, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { userApi } from '@/api/admin/users';
import { ContractApiError, contractApi } from '@/api/commercial/contracts';
import { ProposalStatus, proposalApi } from '@/api/commercial/proposals';
import {
  type ProposalContractViewModel,
  toProposalContractViewModel,
} from '@/components/commercial/proposals/contracts/proposal-contract-types';
import { ProposalContractsDialog } from '@/components/commercial/proposals/contracts/proposal-contracts-dialog';
import { PaymentConfigModal } from '@/components/dialogs/commercial/payment-config/payment-config-modal';
import { ProposalTable } from '@/components/table/commercial/proposal-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableSkeleton } from '@/components/ui/skeleton';
import { useSignedProposalContracts } from '@/hooks/commercial/use-contracts';
import { toast } from '@/hooks/use-toast';
import { downloadBlob, openBlobInNewTab } from '@/lib/utils';

type ProposalStatusFilter = 'Todos' | ProposalStatus;

const ISO_DATE_FORMAT = 'yyyy-MM-dd';

function parseUrlDate(value: string | null): Date | undefined {
  if (!value) return undefined;
  const parsed = parse(value, ISO_DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
}

const STATUS_OPTIONS: { value: ProposalStatusFilter; label: string }[] = [
  { value: 'Todos', label: 'Todos' },
  { value: ProposalStatus.OPEN, label: 'Aberta' },
  { value: ProposalStatus.PENDING_ACCEPT, label: 'Aguardando aceite' },
  { value: ProposalStatus.PENDING_CREDIT, label: 'Aguardando Análise de Crédito' },
  { value: ProposalStatus.FINISHED, label: 'Finalizada' },
  { value: ProposalStatus.CANCELED, label: 'Cancelada' },
  { value: ProposalStatus.REFUSED, label: 'Recusada' },
  { value: ProposalStatus.EXPIRED, label: 'Expirada' },
];

export function ProposalListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentConfigOpen, setPaymentConfigOpen] = useState(false);
  const [contractsProposalId, setContractsProposalId] = useState<string | null>(null);
  // Filter states from URL
  const [filterClient, setFilterClient] = useState(searchParams.get('client') || '');
  const [filterCode, setFilterCode] = useState(searchParams.get('code') || '');
  const [filterStatus, setFilterStatus] = useState<ProposalStatusFilter>(
    (searchParams.get('status') as ProposalStatusFilter) || 'Todos',
  );
  const [filterStartDate, setFilterStartDate] = useState<Date | undefined>(() =>
    parseUrlDate(searchParams.get('start_date')),
  );
  const [filterEndDate, setFilterEndDate] = useState<Date | undefined>(() =>
    parseUrlDate(searchParams.get('end_date')),
  );
  const [filterSeller, setFilterSeller] = useState(searchParams.get('seller') || '');

  const page = Number(searchParams.get('page')) || 1;

  const {
    data: sellers = [],
    isLoading: isLoadingSellers,
    isError: isSellersError,
    refetch: refetchSellers,
  } = useQuery({
    queryKey: ['proposal-sellers'],
    queryFn: async () => {
      const firstPage = await userApi.findMany({ page: 1, limit: 100 });
      const users = [...firstPage.docs];
      for (let page = 2; page <= firstPage.pages; page++) {
        const result = await userApi.findMany({ page, limit: 100 });
        users.push(...result.docs);
      }
      return users.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    },
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['proposals', Object.fromEntries(searchParams)],
    queryFn: () => {
      const params: Record<string, string | number> = {};
      const client = searchParams.get('client');
      const code = searchParams.get('code');
      const status = searchParams.get('status');
      const startDate = searchParams.get('start_date');
      const endDate = searchParams.get('end_date');
      const seller = searchParams.get('seller');
      const pageParam = searchParams.get('page');

      if (client) params.search = client;
      if (code) params.code = Number(code);
      if (status && status !== 'Todos') params.status = status;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (seller) params.seller = seller;
      if (pageParam) params.page = Number(pageParam);

      return proposalApi.findMany(params);
    },
  });

  const finishedProposals = (data?.docs ?? []).filter(
    (proposal) => proposal.status === ProposalStatus.FINISHED,
  );
  const signedContractsQueries = useSignedProposalContracts(finishedProposals);
  const selectedContractsQuery = contractsProposalId
    ? signedContractsQueries.get(contractsProposalId)
    : undefined;
  const selectedProposal = finishedProposals.find(
    (proposal) => proposal.id === contractsProposalId,
  );
  const selectedContracts = (selectedContractsQuery?.data ?? []).map(toProposalContractViewModel);

  const handleViewContract = async (contract: ProposalContractViewModel) => {
    try {
      const file = await contractApi.getPdf(contract.id, contract.version, 'inline');
      if (!openBlobInNewTab(file.blob)) {
        toast({
          title: 'Não foi possível abrir o contrato',
          description: 'Permita pop-ups para visualizar o PDF em uma nova aba.',
          variant: 'destructive',
        });
      }
    } catch (pdfError) {
      toast({
        title: 'Erro ao visualizar contrato',
        description:
          pdfError instanceof ContractApiError
            ? pdfError.message
            : 'Não foi possível concluir a operação.',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadContract = async (contract: ProposalContractViewModel) => {
    try {
      const file = await contractApi.getPdf(contract.id, contract.version, 'attachment');
      downloadBlob(file.blob, file.fileName);
    } catch (pdfError) {
      toast({
        title: 'Erro ao baixar contrato',
        description:
          pdfError instanceof ContractApiError
            ? pdfError.message
            : 'Não foi possível concluir a operação.',
        variant: 'destructive',
      });
    }
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('page', '1');

    if (filterClient) params.set('client', filterClient);
    if (filterCode) params.set('code', filterCode);
    if (filterStatus && filterStatus !== 'Todos') params.set('status', filterStatus);
    if (filterStartDate) params.set('start_date', format(filterStartDate, ISO_DATE_FORMAT));
    if (filterEndDate) params.set('end_date', format(filterEndDate, ISO_DATE_FORMAT));
    if (filterSeller) params.set('seller', filterSeller);

    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilterClient('');
    setFilterCode('');
    setFilterStatus('Todos');
    setFilterStartDate(undefined);
    setFilterEndDate(undefined);
    setFilterSeller('');
    setSearchParams(new URLSearchParams());
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Erro ao carregar propostas</p>
        <Button onClick={() => refetch()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Propostas</h1>
          <p className="text-muted-foreground">Visualize e gerencie as propostas comerciais</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPaymentConfigOpen(true)}>
            <CreditCard className="h-4 w-4 mr-2" />
            Configuração de Pagamento
          </Button>
          <Button asChild className="bg-black text-white hover:bg-gray-800">
            <Link to="/commercial/proposal/form/create">
              <Plus className="h-4 w-4 mr-2" />
              Nova Proposta
            </Link>
          </Button>
        </div>
        <PaymentConfigModal open={paymentConfigOpen} onOpenChange={setPaymentConfigOpen} />
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>Defina os critérios e clique em "Pesquisar"</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div>
                <Input
                  label="Cliente"
                  placeholder="Nome, CPF ou CNPJ"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Identificador"
                  placeholder="ID da proposta"
                  value={filterCode}
                  onChange={(e) => setFilterCode(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="proposal-status"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Status
                </label>
                <Select
                  value={filterStatus}
                  onValueChange={(value) => setFilterStatus(value as ProposalStatusFilter)}
                >
                  <SelectTrigger id="proposal-status">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <DatePickerInput
                  label="Data início"
                  value={filterStartDate}
                  onChange={setFilterStartDate}
                />
              </div>
              <div>
                <DatePickerInput
                  label="Data fim"
                  value={filterEndDate}
                  onChange={setFilterEndDate}
                />
              </div>
              <div>
                <label htmlFor="proposal-seller" className="block text-sm font-medium mb-2">
                  Vendedor
                </label>
                <Select
                  value={filterSeller || 'all'}
                  onValueChange={(value) => setFilterSeller(value === 'all' ? '' : value)}
                  disabled={isLoadingSellers || isSellersError}
                >
                  <SelectTrigger id="proposal-seller">
                    <SelectValue placeholder={isLoadingSellers ? 'Carregando...' : 'Todos'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {filterSeller && !sellers.some((seller) => seller.id === filterSeller) && (
                      <SelectItem value={filterSeller}>{filterSeller}</SelectItem>
                    )}
                    {sellers.map((seller) => (
                      <SelectItem key={seller.id} value={seller.id}>
                        {seller.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isSellersError && (
                  <div className="mt-1 text-sm text-destructive">
                    Não foi possível carregar os vendedores.{' '}
                    <button type="button" className="underline" onClick={() => refetchSellers()}>
                      Tentar novamente
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                <Search className="h-4 w-4 mr-2" />
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : data ? (
        <ProposalTable
          data={data}
          currentPage={page}
          onPageChange={handlePageChange}
          contracts={{
            getContractsForProposal: (proposal) =>
              (signedContractsQueries.get(proposal.id)?.data ?? []).map(
                toProposalContractViewModel,
              ),
            onViewProposalContracts: (proposal) => setContractsProposalId(proposal.id),
          }}
        />
      ) : null}

      <ProposalContractsDialog
        open={contractsProposalId !== null}
        onOpenChange={(open) => {
          if (!open) setContractsProposalId(null);
        }}
        title="Contratos assinados"
        description={
          selectedProposal
            ? `Contratos assinados da proposta ${selectedProposal.seq}.`
            : 'Contratos assinados vinculados à proposta.'
        }
        contracts={selectedContracts}
        isLoading={selectedContractsQuery?.isLoading ?? false}
        error={
          selectedContractsQuery?.error
            ? selectedContractsQuery.error instanceof ContractApiError
              ? selectedContractsQuery.error.message
              : 'Não foi possível carregar os contratos assinados da proposta.'
            : null
        }
        onView={handleViewContract}
        onDownload={handleDownloadContract}
      />
    </motion.div>
  );
}
